import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import api from '../../../api/axios';
import { showSuccess, showError } from '../../../utils/toast';
import { useLoading } from '../../../components/LoadingProvider';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

function DoctorAppointmentSlots() {
    const navigate = useNavigate();
    const { user } = useAuth({ redirectPath: '/login', allowedRoles: ['doctor'] });
    const { setIsLoading } = useLoading();

    const today = new Date();
    today.setDate(today.getDate() + 1);
    const [selectedDate, setSelectedDate] = useState(today.toISOString().substring(0, 10));

    const [currentTab, setCurrentTab] = useState('add-slots');
    const [errorAddSlot, setErrorAddSlot] = useState(null);
    const [bookedSlots, setBookedSlots] = useState([]);
    const [freeSlots, setFreeSlots] = useState([]);
    const MySwal = withReactContent(Swal);

    const [addSlots, setAddSlots] = useState([{
        startTime: '',
        endTime: ''
    },{
        startTime: '',
        endTime: ''
    }]);

    const [previewDates, setPreviewDates] = useState([]);
    const [previewStartTime, setPreviewStartTime] = useState('');
    const [previewEndTime, setPreviewEndTime] = useState('');
    const [previewSlotDuration, setPreviewSlotDuration] = useState(15);
    const [previewSlots, setPreviewSlots] = useState([]);

    const getSlots = useCallback(async () => {
        try{
            setIsLoading(true);
            const response = await api.get('/doctor/appointment-slots', {
                params: {
                    date: selectedDate
                }
            });
            if (response?.data?.result === 'success') {
                setBookedSlots(response.data.data.slots.bookedSlots);
                setFreeSlots(response.data.data.slots.freeSlots);
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            }else{
                setBookedSlots([]);
                setFreeSlots([]);
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error getting slots');
            setBookedSlots([]);
            setFreeSlots([]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedDate, setIsLoading]);

    useEffect(() => {
        document.title = 'Varosa - Doctor Appointment Slots';
        // Check authentication status
        if (!user) {
            navigate('/login');
            return;
        }
        getSlots();
    }, [user, navigate, getSlots]);

    const isDateGreaterOrEqualToday = (inputDate) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to compare only dates
        
        const input = new Date(inputDate);
        input.setHours(0, 0, 0, 0); // Ensure time doesn't affect comparison
        
        return input >= today;
    }

    const handleSubmitAddSlots = async () => {
        if (!isDateGreaterOrEqualToday(selectedDate)) {
            setErrorAddSlot('Date must be greater than or equal to today');
            return;
        }
        for (let i = 0; i < addSlots.length; i++) {
            if (addSlots[i].startTime === '' || addSlots[i].endTime === '') {
                setErrorAddSlot('Please fill in all slots');
                return;
            }
        }
        try{
            setErrorAddSlot(null);
            setIsLoading(true);
            const postData = {
                date: selectedDate,
                slots: addSlots.map(slot => ({
                    startTime: slot.startTime,
                    endTime: slot.endTime
                }))
            }
            const response = await api.post('/doctor/appointment-slots', postData);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                setAddSlots([{
                    startTime: '',
                    endTime: ''
                },{
                    startTime: '',
                    endTime: ''
                }]);
                getSlots();
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            }else{
                showError(response.data.message);
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error adding slots');
        } finally {
            setIsLoading(false);
        }
    }

    const handleDeleteSlot = async (slotId) => {
        try{
            setIsLoading(true);
            const response = await api.delete(`/doctor/appointment-slots/${slotId}`);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                getSlots();
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            }else{
                showError(response.data.message);
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error deleting slot');
        } finally {
            setIsLoading(false);
        }
    }

    const openDeleteSlotModal = (slotId) => {
        if (!isDateGreaterOrEqualToday(selectedDate)) {
            MySwal.fire({
                title: 'Error',
                text: 'Date must be greater than or equal to today',
                icon: 'error',
                confirmButtonText: 'OK'
            });
            return;
        }
        MySwal.fire({
            title: 'Are you sure, you want to delete this slot?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeleteSlot(slotId);
            }
        });
    }

    const generatePreviewSlots = () => {
        if (!previewDates || !previewStartTime || !previewEndTime || !previewSlotDuration) {
            showError('Please fill in all fields');
            return;
        }

        const duration = parseInt(previewSlotDuration, 10);
        if (Number.isNaN(duration) || duration < 5 || duration > 60) {
            showError('Slot duration must be between 5 and 60 minutes');
            return;
        }

        const dates = previewDates;
        if (dates.length === 0) {
            showError('Please enter at least one date');
            return;
        }

        if (previewStartTime >= previewEndTime) {
            showError('Start time must be before end time');
            return;
        }

        const slots = [];
        let slotIndex = 0;

        dates.forEach((date) => {
            let startTime = previewStartTime.split(':');
            let currentTime = new Date(date.setHours(startTime[0], startTime[1], 0, 0));
            let endTime = previewEndTime.split(':');
            const endDateTime = new Date(date.setHours(endTime[0], endTime[1], 0, 0));

            while (currentTime < endDateTime) {
                const slotEnd = new Date(currentTime);
                slotEnd.setMinutes(slotEnd.getMinutes() + duration);

                if (slotEnd > endDateTime) {
                    break;
                }

                slots.push({
                    id: slotIndex++,
                    date: date.getDate()+'-'+(date.getMonth()+1)+'-'+date.getFullYear(),
                    startTime: currentTime.toTimeString().slice(0, 5),
                    endTime: slotEnd.toTimeString().slice(0, 5)
                });

                currentTime.setMinutes(currentTime.getMinutes() + previewSlotDuration);
            }
        });

        if (slots.length === 0) {
            showError('No slots generated for the selected time range');
            return;
        }

        setPreviewSlots(slots);
    };

    const removePreviewSlot = (slotId) => {
        setPreviewSlots(prev => prev.filter(slot => slot.id !== slotId));
    };

    const handleSubmitPreviewSlots = async () => {
        if (previewSlots.length === 0) {
            showError('No slots to submit');
            return;
        }

        try{
            setErrorAddSlot(null);
            setIsLoading(true);
            const response = await api.post('/doctor/appointment-multiple-slots', {
                slots: previewSlots
            });
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                setPreviewSlots([]);
                setPreviewDates([]);
                setPreviewStartTime('');
                setPreviewEndTime('');
                getSlots();
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            }else{
                showError(response.data.message);
            }

        } catch (error) {
            showError(error.response?.data?.message || 'Error adding slots');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="container py-5 login-container">
                <div className="row g-3">
                    <div className="col-12">
                        <h2>Appointment Slots</h2>
                    </div>
                </div>
            </div>
            <div className="container-fluid feature bg-light py-5" id="findDoctor">
                <div className="container">
                    <div className="row g-4 justify-content-center">
                        <div className="col-md-12 justify-content-center align-items-center text-center col-sm-12">
                            <div className="row">
                                <div className="offset-md-3 col-md-6">
                                    <label htmlFor="date" className="form-label">Select Date</label>
                                    <input 
                                        max={new Date(today.getFullYear(), today.getMonth(), today.getDate() + 30).toISOString().substring(0, 10)}
                                        onChange={(e) => {
                                            setSelectedDate(e.target.value);
                                        }}
                                        type="date" 
                                        id="date"
                                        className="form-control py-3 border-primary bg-transparent" 
                                        placeholder="Select Date" 
                                        defaultValue={selectedDate} />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-12 wow fadeInUp slots" data-wow-delay="0.1s">
                            <ul className="nav nav-tabs nav-justified">
                                <li className="nav-item">
                                    <a className={`nav-link ${currentTab === 'add-slots' ? 'active' : ''}`} onClick={() => setCurrentTab('add-slots')} href="#">Add Slots</a>
                                </li>
                                <li className="nav-item">
                                    <a className={`nav-link ${currentTab === 'booked-slots' ? 'active' : ''}`} onClick={() => setCurrentTab('booked-slots')} href="#">Booked Slots</a>
                                </li>
                                <li className="nav-item">
                                    <a className={`nav-link ${currentTab === 'free-slots' ? 'active' : ''}`} onClick={() => setCurrentTab('free-slots')} href="#">Free Slots</a>
                                </li>
                            </ul>
                            <div className="tab-content background-light px-3 py-3">
                                <div className={`tab-pane fade add-slots ${currentTab === 'add-slots' ? 'show active' : ''}`}>
                                    <div className="row g-4">
                                        {errorAddSlot && (
                                            <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                                {errorAddSlot}
                                                <button type="button" className="btn-close" onClick={() => setErrorAddSlot(null)} aria-label="Close"></button>
                                            </div>
                                        )}
                                        {addSlots.map((slot, index) => (
                                            <div className="col-md-6" key={index}>
                                                <div className="input-group">
                                                    <input type="time" className="form-control py-3 border-primary bg-transparent" value={slot.startTime} onChange={(e) => {
                                                        const newSlots = [...addSlots];
                                                        newSlots[index].startTime = e.target.value;
                                                        let time = e.target.value.split(':');
                                                        time[1] = parseInt(time[1]) + 15;
                                                        if(time[1] >= 60){
                                                            time[0] = parseInt(time[0]) + 1;
                                                            time[1] -= 60;
                                                            time[0] = time[0] < 10 ? '0' + time[0] : time[0];
                                                            time[1] = time[1] < 10 ? '0' + time[1] : time[1];
                                                        }
                                                        newSlots[index].endTime = time[0] + ':' + time[1];
                                                        setAddSlots(newSlots);
                                                    }} />
                                                    <span className="input-group-text">:</span>
                                                    <input type="time" className="form-control py-3 border-primary bg-transparent" value={slot.endTime} onChange={(e) => {
                                                        const newSlots = [...addSlots];
                                                        newSlots[index].endTime = e.target.value;
                                                        setAddSlots(newSlots);
                                                    }} />
                                                    <button className="btn btn-outline-primary" type="button" onClick={() => {
                                                        const newSlots = [...addSlots];
                                                        newSlots.splice(index, 1);
                                                        if(newSlots.length === 0){
                                                            setAddSlots([{
                                                                startTime: '',
                                                                endTime: ''
                                                            }]);
                                                        }else{
                                                            setAddSlots(newSlots);
                                                        }
                                                    }}><i className="bi bi-dash-circle"></i></button>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="col-md-12 text-end mt-3">
                                            <button 
                                                className="btn btn-outline-primary py-2 border-primary bg-transparent" 
                                                type="button" 
                                                onClick={() => setAddSlots([...addSlots, {
                                                    startTime: '',
                                                    endTime: ''
                                                }])}
                                            >Add</button>
                                        </div>
                                        <div className="col-md-12">
                                            <button className="btn btn-primary" onClick={handleSubmitAddSlots}>Submit</button>
                                        </div>
                                    </div>
                                </div>
                                <div className={`tab-pane fade booked-slots ${currentTab === 'booked-slots' ? 'show active' : ''}`}>
                                    <div className="row g-4 justify-content-center">
                                        {bookedSlots.map((slot, index) => (
                                            <div className="col-md-4 wow fadeInUp" data-wow-delay="0.1s" key={slot.id}>
                                                <div className="alert alert-info fade show" role="alert">
                                                    <b>{slot.startTime} - {slot.endTime}</b>
                                                </div>
                                            </div>
                                        ))}
                                        {bookedSlots.length === 0 && (
                                            <div className="col-md-12">
                                                <p className="text-center">No booked slots found</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className={`tab-pane fade free-slots ${currentTab === 'free-slots' ? 'show active' : ''}`}>
                                    <div className="row g-4">
                                        {freeSlots.map((slot, index) => (
                                            <div className="col-md-4 wow fadeInUp" data-wow-delay="0.1s" key={slot.id}>
                                                <div className="alert alert-info alert-dismissible fade show" role="alert">
                                                    <b>{slot.startTime} - {slot.endTime}</b>
                                                    <button type="button" className="btn-close" onClick={() => { openDeleteSlotModal(slot.id) }} aria-label="Close"></button>
                                                </div>
                                            </div>
                                        ))}
                                        {freeSlots.length === 0 && (
                                            <div className="col-md-12">
                                                <p className="text-center">No free slots found</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-12 wow fadeInUp generate-multiple-slots" data-wow-delay="0.1s">
                            <div className="background-light px-3 py-3">
                                <div className="row g-4">
                                    <div className="col-12">
                                        <h4 className="mb-0">Generate Multiple Slots</h4>
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="preview_dates" className="form-label">Dates</label>
                                        <div className="slot-datepicker-div">
                                            <DatePicker
                                                className="form-control w-100 py-3 border-primary bg-transparent"
                                                selectsMultiple
                                                showIcon 
                                                selectedDates={previewDates}
                                                onChange={(dates) => setPreviewDates(dates)}
                                                shouldCloseOnSelect={false}
                                                disabledKeyboardNavigation
                                                dateFormat="dd/MM/yyyy"
                                                minDate={new Date()}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <label htmlFor="preview_start_time" className="form-label">Start Time</label>
                                        <input
                                            type="time"
                                            id="preview_start_time"
                                            className="form-control py-3 border-primary bg-transparent"
                                            value={previewStartTime}
                                            onChange={(e) => setPreviewStartTime(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label htmlFor="preview_end_time" className="form-label">End Time</label>
                                        <input
                                            type="time"
                                            id="preview_end_time"
                                            className="form-control py-3 border-primary bg-transparent"
                                            value={previewEndTime}
                                            onChange={(e) => setPreviewEndTime(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label htmlFor="preview_slot_duration" className="form-label">Duration</label>
                                        <input
                                            type="number"
                                            id="preview_slot_duration"
                                            className="form-control py-3 border-primary bg-transparent"
                                            min="5"
                                            max="60"
                                            value={previewSlotDuration}
                                            onChange={(e) => setPreviewSlotDuration(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-12 text-end">
                                        <button className="btn btn-outline-primary me-2" type="button" onClick={generatePreviewSlots}>
                                            Preview Slots
                                        </button>
                                        {previewSlots.length > 0 && (
                                            <button className="btn btn-primary" type="button" onClick={handleSubmitPreviewSlots}>
                                                Submit Preview Slots
                                            </button>
                                        )}
                                    </div>
                                    {previewSlots.length > 0 && (
                                        <div className="col-md-12">
                                            <div className="table-responsive">
                                                <table className="table table-sm table-bordered align-middle">
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Start Time</th>
                                                            <th>End Time</th>
                                                            <th className="text-center">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {previewSlots.map((slot) => (
                                                            <tr key={slot.id}>
                                                                <td>{slot.date}</td>
                                                                <td>{slot.startTime}</td>
                                                                <td>{slot.endTime}</td>
                                                                <td className="text-center">
                                                                    <button
                                                                        className="btn btn-danger btn-sm"
                                                                        type="button"
                                                                        onClick={() => removePreviewSlot(slot.id)}
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <p className="mt-3 text-success fw-bold">Total Slots: {previewSlots.length}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default DoctorAppointmentSlots;
